import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2Icon, ChevronDownIcon, StarIcon, XIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ReviewsGallery } from '../components/ReviewsGallery';
import { FAQSection } from '../components/FAQSection';
export function CrescOfferPage() {
  const [activeTab, setActiveTab] = useState<'standard' | 'premium'>('standard');
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: 'Cât costă programul?',
    a: 'Programul CRESC Standard este 580 lei pe lună (6 luni) sau 2.985 lei plată integrală. Programul CRESC Premium, care include și 6 sesiuni individuale cu mine, este 4.495 lei plată integrală.'
  }, {
    q: 'Dacă nu funcționează pentru copilul meu?',
    a: 'Nu vorbim despre "funcționează" sau "nu funcționează", ci despre cât de constant aplici și cât de potrivite sunt strategiile pentru nivelul copilului tău. Cursul te ajută inclusiv să îți dai seama când este cazul să ceri și alte tipuri de ajutor, astfel încât totul să fie cu succes.'
  }, {
    q: 'Dacă pe parcurs decid să mă retrag?',
    a: 'Dacă ai plata lunară, vei avea acces la modulele de până la momentul retragerii. Dacă ai plata integrală, vei avea acces la tot cursul, pentru a-l putea relua atunci când te simți pregătită.'
  }, {
    q: 'Cât timp pe zi trebuie să aloc?',
    a: 'Nu ai nevoie de ore separate de terapie. Lucrul se face în viața de zi cu zi: în joc, la masă, în baie, în plimbare. Ceea ce schimbăm este modul în care interacționezi cu copilul tău. Nu adaugi ceva în plus, ci faci diferit ce faci deja.'
  }, {
    q: 'În cât timp voi vedea rezultate?',
    a: 'Depinde de copil și de cât de consecventă ești. Pentru unele abilități sunt suficiente câteva săptămâni, pentru altele, cum este limbajul, este nevoie de câteva luni de muncă consecventă. Programul durează 6 luni tocmai pentru a acoperi tot ce are copilul nevoie.'
  }, {
    q: 'Este potrivit dacă copilul meu are deja un diagnostic?',
    a: 'Da, programul este creat și pentru mame de copii cu diagnostic de autism, întârziere în dezvoltare sau alte tulburări. Tot ce înveți completează terapia din centru și te ajută să lucrezi acasă între ședințe. Dacă copilul are tulburări asociate complexe, te ghidez și spre ajutor suplimentar.'
  }, {
    q: 'Ce diferență este între Standard și Premium?',
    a: 'Pachetul Standard include toate cursurile video, apelurile săptămânale de grup și materialele practice. Pachetul Premium adaugă 6 sesiuni individuale 1:1 cu mine, în care lucrăm direct pe situația copilului tău, cu plan personalizat și ghidare adaptată la ritmul lui.'
  }];
  // Comparație pachete — listă completă de componente, grupate
  const baseFeatures = ['6 cursuri video înregistrate, deblocate progresiv', 'Apel săptămânal de grup cu Cristina (6 luni)', 'Materiale practice și PDF-uri de lucru', 'Acces la comunitatea de mame din program'];
  const premiumFeatures = ['6 sesiuni individuale 1:1 cu Cristina', 'Plan personalizat pentru copilul tău', 'Suport direct pentru situațiile tale specifice'];
  const featureGroups = [{
    title: 'Baza programului',
    features: baseFeatures
  }, {
    title: 'Extensii Premium',
    features: premiumFeatures
  }];
  const packages: {
    key: 'standard' | 'premium';
    name: string;
    included: string[];
    oldPrice: string;
    price: string;
    note: string;
    cta: string;
    recommended?: boolean;
  }[] = [{
    key: 'standard',
    name: 'Pachet Standard',
    included: baseFeatures,
    oldPrice: '3.480 lei',
    price: '2.985 lei',
    note: 'Plată integrală. Sau 580 lei / lună × 6.',
    cta: 'Vreau pachetul Standard'
  }, {
    key: 'premium',
    name: 'Pachet Premium',
    included: [...baseFeatures, ...premiumFeatures],
    oldPrice: '5.190 lei',
    price: '4.495 lei',
    note: 'Plată integrală. Sau 865 lei / lună × 6.',
    cta: 'Vreau pachetul Premium',
    recommended: true
  }];
  const modules = [{
    id: '01',
    title: 'Cunoașterea Dezvoltării',
    desc: 'Primul pas este să înțelegi cum se dezvoltă cu adevărat un copil mic, ce e normal și ce nu, fără să te mai bazezi pe informații de pe internet sau pe părerea celorlalți.',
    image: 'https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=800&q=80',
    bullets: ['Care sunt etapele reale de dezvoltare și ce ar trebui să facă copilul tău la vârsta lui', 'Diferența dintre o variație normală și o întârziere care necesită atenție', 'Semnele de alarmă pe care merită să le cunoști, fără să te panichezi', 'Rolul mediului și al rutinelor zilnice în stimularea dezvoltării', 'Primele tehnici simple de stimulare a limbajului, pe care le poți aplica imediat', 'Când e suficient să observi și când e nevoie de o evaluare profesională']
  }, {
    id: '02',
    title: 'Relaționare și Joc',
    desc: 'Înainte ca un copil să vorbească, are nevoie să se conecteze cu oamenii din jurul lui. Acest curs te învață cum să construiești acea conexiune, prin joc și interacțiune naturală.',
    image: 'https://images.unsplash.com/photo-1471286174890-9c112ffca5b4?w=800&q=80',
    bullets: ['Cum să obții și să menții contactul vizual cu copilul tău', 'Ce este atenția împărtășită și de ce este baza comunicării', 'Cum să îl înveți pe copil să imite gesturi, sunete și acțiuni', 'Cum să trezești interesul copilului pentru oameni, nu doar pentru obiecte', 'Tipuri de joc potrivite fiecărei etape de dezvoltare', 'Cum să transformi momentele de joc în momente de învățare']
  }, {
    id: '03',
    title: 'Emoții și Reglare',
    desc: 'Crizele de nervi, refuzul, plânsul intens. Acest curs te ajută să înțelegi ce se întâmplă în spatele comportamentelor copilului tău și cum să îl ghidezi fără să te simți depășită.',
    image: 'https://images.unsplash.com/photo-1531983412531-1f49a365ffed?w=800&q=80',
    bullets: ['De ce copilul tău face crize de nervi și ce încearcă să îți comunice prin ele', 'Cum să îl ajuți să tolereze frustrarea pas cu pas', 'Cum să gestionezi momentele în care refuză totul', 'Tehnici simple de reglare emoțională potrivite vârstei lui', 'Cum să pui limite fără să te simți vinovată', 'Diferența dintre un capriciu și o nevoie reală neexprimată']
  }, {
    id: '04',
    title: 'Structurarea Comunicării',
    desc: 'Aici lucrezi direct pe limbaj: de la a înțelege ce i se spune, la a cere ce are nevoie, la primele propoziții. Tot ce faci se întâmplă în viața de zi cu zi, nu în ședințe separate de "terapie".',
    image: 'https://images.unsplash.com/photo-1587654780291-39c9404d7dd0?w=800&q=80',
    bullets: ['Cum să stimulezi limbajul receptiv, adică înțelegerea a ce i se spune', 'Cum să îl ajuți pe copil să ceară ce are nevoie în loc să plângă sau să arate', 'Pașii concreti de la primele sunete la primele cuvinte și propoziții', 'Cum să transformi rutinele zilnice (masa, baia, plimbarea) în momente de stimulare a limbajului', 'Cum să răspunzi când copilul comunică, ca să îl încurajezi să continue', 'Când comunicarea funcțională contează mai mult decât numărul de cuvinte']
  }, {
    id: '05',
    title: 'Comportament și Autonomie',
    desc: 'Ultimul pilon pregătește copilul pentru viața reală: autoservire, rutine, reguli și pregătirea pentru grădiniță. Îl ajuți să devină mai independent, pas cu pas.',
    image: 'https://images.unsplash.com/photo-1596464716127-f2a82984de30?w=800&q=80',
    bullets: ['Cum să îl înveți pe copil să mănânce singur, să se îmbrace și să coopereze la rutinele zilnice', 'Cum să începi antrenamentul la toaletă fără presiune și fără eșecuri repetate', 'Cum să creezi rutine clare pe care copilul le înțelege și le respectă', 'Cum să îl ajuți să stea în sarcină și să respecte reguli simple', 'Cum să îl pregătești pentru grădiniță și pentru interacțiunea cu alți copii', 'Responsabilități potrivite vârstei pe care copilul le poate învăța acum']
  }, {
    id: '06',
    title: 'Re-evaluare și Următorii Pași',
    desc: 'Luna finală a programului. Faci bilanțul a ceea ce ai realizat în 5 luni, clarifici ce mai ai de lucrat și primești direcția clară pentru următoarea etapă a copilului tău.',
    image: 'https://images.unsplash.com/photo-1516627145497-ae6968895b74?w=800&q=80',
    bullets: ['Cum să evaluezi progresul copilului tău față de punctul de plecare', 'Ce abilități s-au consolidat și unde mai e nevoie de lucru', 'Cum să continui acasă după terminarea programului', 'Când și cum să accesezi ajutor profesional suplimentar, dacă e cazul', 'Cum să te pregătești pentru provocările următoarei etape de dezvoltare', 'Un plan clar pentru lunile care urmează, adaptat situației copilului tău']
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="pt-[64px] md:pt-[72px]" data-id="element-1007">
      {/* SECȚIUNEA 1 — HERO OFERTĂ */}
      <section className="bg-brand-navy section-padding text-white" data-id="element-1008">
        <div className="container-custom" data-id="element-1009">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-1010">
            <div className="max-w-xl" data-id="element-1011">
              <h1 className="font-playfair text-[48px] md:text-[64px] font-bold text-white mb-6 leading-[1.1]" data-id="element-1012">
                Uite cum CRESC!
              </h1>
              <p className="font-source text-[20px] md:text-[24px] text-brand-gold mb-10 italic" data-id="element-1013">
                Colecția completă de cursuri pentru dezvoltarea armonioasă a
                copilului tău, pas cu pas, prin joc și conectare.
              </p>
              <a href="#inscriere" className="btn-inverted text-[18px] px-8 py-4" data-id="element-1014">
                Click aici pentru înscriere!
              </a>
            </div>
            <div className="relative rounded-[24px] overflow-hidden shadow-2xl border-4 border-white/10" data-id="element-1015">
              <img src="https://assets.cdn.filesafe.space/3SQqBp51ZHIpTGzJ3Vu1/media/6a2bf33308984b712926c13b.jpg" alt="Programul CRESC" className="w-full h-full object-cover aspect-[4/3]" data-id="element-1016" />
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 2 — DESCRIERE */}
      <section className="bg-brand-beige section-padding" data-id="element-1017">
        <div className="container-custom" data-id="element-1018">
          <div className="max-w-3xl mx-auto" data-id="element-1019">
            <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy mb-8 text-center" data-id="element-1020">
              Copilul tău nu vorbește, iar tu nu mai știi ce să faci?
            </h2>
            <div className="font-source text-[16px] md:text-[18px] text-brand-text space-y-6" data-id="element-1021">
              <p data-id="element-1022">
                Ești mama unui copil între 1 și 4 ani care nu vorbește sau
                vorbește prea puțin. Poate a primit un diagnostic, poate ai
                observat tu că e diferit de ceilalți copii. Ai căutat pe
                internet, ai întrebat pe grupuri, ai ascultat sfaturi de la
                apropiați care îți spun că "exagerezi" și că "o să vorbească și
                el". Dar în interior simți că ceva nu e în regulă și că timpul
                trece.
              </p>
              <p data-id="element-1023">
                Fiecare lună de așteptare poate adânci problema. Cursurile
                cumpărate la întâmplare nu au funcționat. Ghidurile gratuite îți
                dau informații trunchiate. Între timp, copilul tău crește, iar
                fereastra în care poți face cea mai mare diferență se
                îngustează.
              </p>
              <div className="bg-white p-6 rounded-[16px] border-l-4 border-brand-coral" data-id="element-1024">
                <p className="font-medium text-brand-navy" data-id="element-1025">
                  Programul "Uite cum CRESC!" este colecția completă de cursuri
                  prin care îți arăt, pas cu pas, cum să îți ajuți copilul să se
                  dezvolte pe toate ariile care contează: înțelegere,
                  comunicare, joc, emoții și autonomie. Nu ai nevoie de tehnici
                  complicate sau jucării scumpe. Ai nevoie de un plan clar și de
                  ghidare reală.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 3B — COLECȚIA DE CURSURI */}
      <section className="bg-brand-ivory section-padding" data-id="element-1026">
        <div className="container-custom" data-id="element-1027">
          <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy text-center mb-16" data-id="element-1028">
            Ce conține Colecția Completă "Uite cum CRESC!"
          </h2>
          <div className="max-w-6xl mx-auto space-y-12" data-id="element-1029">
            {modules.map((mod, index) => {
            const isEven = index % 2 === 0;
            return <div key={mod.id} className={`flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} bg-white rounded-[32px] shadow-sm border border-gray-100 overflow-hidden`} data-id="element-1030">
                  {/* Image Side */}
                  <div className="w-full md:w-1/2 relative min-h-[300px] md:min-h-[400px]" data-id="element-1031">
                    <img src={mod.image} alt={mod.title} className="absolute inset-0 w-full h-full object-cover" data-id="element-1032" />
                    {/* Premium Badge */}
                    <div className={`absolute top-6 ${isEven ? 'left-6' : 'right-6'} w-16 h-16 rounded-full bg-brand-gold flex items-center justify-center shadow-lg`} data-id="element-1033">
                      <span className="font-playfair font-bold text-[24px] text-white" data-id="element-1034">
                        {mod.id}
                      </span>
                    </div>
                  </div>

                  {/* Content Side */}
                  <div className="w-full md:w-1/2 p-8 md:p-12 flex flex-col justify-center" data-id="element-1035">
                    <span className="font-montserrat font-bold text-[14px] text-brand-gold uppercase tracking-widest mb-2" data-id="element-1036">
                      Cursul {mod.id}
                    </span>
                    <h3 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy mb-6 leading-[1.2]" data-id="element-1037">
                      {mod.title}
                    </h3>
                    <p className="font-source text-[16px] text-brand-text mb-8" data-id="element-1038">
                      {mod.desc}
                    </p>
                    <ul className="space-y-4" data-id="element-1039">
                      {mod.bullets.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-1040">
                          <CheckCircle2Icon className="text-brand-gold flex-shrink-0 mt-1" size={20} data-id="element-1041" />
                          <span className="font-source text-[15px] text-brand-text" data-id="element-1042">
                            {item}
                          </span>
                        </li>)}
                    </ul>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 4 — BENEFICII */}
      <section className="bg-brand-beige section-padding" data-id="element-1043">
        <div className="container-custom" data-id="element-1044">
          <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy text-center mb-12" data-id="element-1045">
            Ce se schimbă concret pentru tine și pentru copilul tău
          </h2>
          <div className="max-w-3xl mx-auto bg-white p-8 md:p-12 rounded-[24px] shadow-sm border-t-4 border-brand-gold" data-id="element-1046">
            <ul className="space-y-8" data-id="element-1047">
              {[{
              title: 'Copilul devine mai prezent și mai conectat',
              desc: 'Începe să te caute prin casă, să te privească, să fie interesat de tine și de jucării. Nu mai stă "în lumea lui".'
            }, {
              title: 'Apar primele cuvinte și primele cereri',
              desc: 'Copilul începe să imite sunete, să ceară ce are nevoie și să răspundă la ce i se spune. Comunicarea devine funcțională.'
            }, {
              title: 'Crizele de nervi se reduc în intensitate și frecvență',
              desc: 'Înveți să anticipezi și să gestionezi momentele dificile. Copilul învață să tolereze frustrarea, iar tu nu te mai simți depășită.'
            }, {
              title: 'Știi exact ce e normal și ce nu',
              desc: 'Nu te mai bazezi pe părerea vecinilor sau pe căutări pe internet. Ai cunoștințele necesare să înțelegi unde e copilul tău și ce are nevoie acum.'
            }, {
              title: 'Ai un plan clar, nu mai cauți la întâmplare',
              desc: 'Fiecare lună știi exact ce să faci, în ce ordine și de ce. Nu mai pierzi timp cu soluții care nu se potrivesc copilului tău.'
            }, {
              title: 'Lucrezi în rutinele zilnice, nu în "ore de terapie"',
              desc: 'Nu ai nevoie de timp separat pentru exerciții. Stimularea se întâmplă la masă, în baie, la joacă, în plimbare. Schimbi modul în care interacționezi cu copilul.'
            }, {
              title: 'Devii mai sigură pe tine ca mamă',
              desc: 'Nu mai întrebi pe grupuri de Facebook și nu mai aștepți validare de la apropiați. Știi ce faci, de ce faci și vezi rezultatele.'
            }, {
              title: 'Copilul e mai pregătit pentru grădiniță și viața socială',
              desc: 'Autoservirea, rutinele, respectarea regulilor și interacțiunea cu alți copii devin posibile. Îl pregătești pentru următoarea etapă a vieții lui.'
            }].map((card, i) => <li key={i} className="flex items-start gap-4" data-id="element-1048">
                  <CheckCircle2Icon className="text-brand-gold flex-shrink-0 mt-1" size={24} data-id="element-1049" />
                  <div data-id="element-1050">
                    <h3 className="font-montserrat font-bold text-[18px] text-brand-navy mb-2" data-id="element-1051">
                      {card.title}
                    </h3>
                    <p className="font-source text-[16px] text-brand-text" data-id="element-1052">
                      {card.desc}
                    </p>
                  </div>
                </li>)}
            </ul>
            <div className="mt-10 text-center" data-id="element-1053">
              <a href="#inscriere" className="btn-primary inline-block" data-id="element-1054">
                Click aici pentru înscriere!
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 5 — CINE SUNT EU */}
      <section className="bg-brand-ivory section-padding" data-id="element-1055">
        <div className="container-custom" data-id="element-1056">
          <div className="max-w-4xl mx-auto" data-id="element-1057">
            <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy text-center mb-10" data-id="element-1058">
              Cine sunt eu și de ce am creat acest program
            </h2>
            <div className="flex flex-col-reverse md:grid md:grid-cols-3 gap-8 items-stretch" data-id="element-1059">
              <div className="md:col-span-2 font-source text-[16px] md:text-[18px] text-brand-text space-y-4 text-center md:text-left flex flex-col justify-center" data-id="element-1060">
                <p data-id="element-1061">
                  Sunt Cristina Geană, psiholog clinician cu peste 11 ani de
                  experiență directă cu copii mici și familiile lor. Am lucrat
                  cu sute de copii cu întârzieri în dezvoltare și tulburări din
                  spectrul autist, iar astăzi coordonez o echipă de 21 de
                  specialiști într-un centru de psihologie și terapie.
                </p>
                <p data-id="element-1062">
                  Am creat Programul CRESC pentru că am văzut cum mamele pierd
                  luni întregi căutând soluții pe internet, cumpărând cursuri
                  care nu funcționează și ascultând sfaturi contradictorii de la
                  apropiați. Am văzut cum copiii pierd timp prețios doar pentru
                  că mama nu a avut pe cineva care să îi arate clar ce să facă.
                </p>
                <p data-id="element-1063">
                  Știu exact ce e normal și ce nu la fiecare vârstă, nu din
                  cărți, ci din experiența directă cu copiii și din ceea ce am
                  învățat ca mamă a doi băieți. Acest program conține tot ce am
                  învățat în 11 ani, structurat într-un mod simplu, pe care
                  orice mamă îl poate aplica acasă, prin joc și conectare.
                </p>
              </div>
              <div className="md:col-span-1" data-id="element-1064">
                <img src="https://cdn.magicpatterns.com/uploads/wvUBHAy6VhqaRqNiVJPB6X/Cristina_poze_%285%29.jpg" alt="Cristina Geană" className="w-full h-full object-cover object-top rounded-[24px] shadow-card" data-id="element-1065" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 6 — PENTRU CINE ESTE / NU ESTE */}
      <section className="bg-brand-navy section-padding text-white" data-id="element-1066">
        <div className="container-custom" data-id="element-1067">
          <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-white text-center mb-12" data-id="element-1068">
            Pentru cine este acest program?
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-1069">
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-[24px] border border-white/20" data-id="element-1070">
              <h3 className="font-playfair text-[28px] font-bold text-brand-gold mb-6 !text-brand-gold" data-id="element-1071">
                Pentru cine este
              </h3>
              <ul className="space-y-4" data-id="element-1072">
                {['Mame de copii între 1 și 4 ani care nu vorbesc sau vorbesc foarte puțin și care vor să înceapă să lucreze acasă cu copilul lor', 'Mame de copii care au fost supraexpuși la ecrane și care au observat întârzieri în dezvoltare față de alți copii de aceeași vârstă', 'Mame de copii cu tulburare de dezvoltare sau diagnostic recent, care nu au acces la terapie în centru sau care vor să facă mai mult acasă între ședințe'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-1073">
                    <CheckCircle2Icon className="text-brand-gold flex-shrink-0 mt-0.5" size={20} data-id="element-1074" />
                    <span className="font-source text-[15px] text-white/90" data-id="element-1075">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
            <div className="bg-white/5 p-8 rounded-[24px] border border-white/10" data-id="element-1076">
              <h3 className="font-playfair text-[28px] font-bold text-white mb-6 !text-white" data-id="element-1077">
                Pentru cine NU este
              </h3>
              <ul className="space-y-4" data-id="element-1078">
                {['Mame care nu sunt dispuse să lucreze zilnic cu copilul lor. Programul funcționează doar dacă aplici constant ce înveți', 'Mame care nu acceptă că copilul lor ar putea avea o întârziere și care caută doar confirmare că "e răsfățat" sau "o să treacă"', 'Mame ai căror copii au tulburări asociate complexe care necesită în primul rând intervenție medicală specializată'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-1079">
                    <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1080">
                      <span className="w-1.5 h-1.5 rounded-full bg-white/60" data-id="element-1081"></span>
                    </div>
                    <span className="font-source text-[15px] text-white/70" data-id="element-1082">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 7 — OFERTĂ (COMPARAȚIE PACHETE) */}
      <section className="bg-brand-beige section-padding" data-id="element-1083">
        <div className="container-custom" data-id="element-1084">
          <div className="text-center mb-12 md:mb-16" data-id="element-1085">
            <span className="font-montserrat font-bold text-[13px] text-brand-gold uppercase tracking-[0.2em] mb-4 block" data-id="element-1086">
              Investiție
            </span>
            <h2 className="font-playfair text-[32px] md:text-[44px] font-bold text-brand-navy mb-4 italic leading-[1.15]" data-id="element-1087">
              Hai să facem o recapitulare a ceea ce primești
            </h2>
            <p className="font-source text-[16px] md:text-[18px] text-brand-text max-w-2xl mx-auto" data-id="element-1088">
              Vezi exact ce este inclus în fiecare pachet și ce adaugă în plus
              varianta Premium.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto items-stretch" data-id="element-1089">
            {packages.map(pkg => <div key={pkg.key} className={`relative flex flex-col rounded-[24px] p-8 md:p-10 text-white bg-[#3d0f1a] ${pkg.recommended ? 'ring-2 ring-brand-gold shadow-2xl md:-mt-4 md:mb-4' : 'ring-1 ring-white/10 shadow-xl'}`} data-id="element-1090">
                {pkg.recommended && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-gold text-brand-navy font-montserrat font-bold text-[12px] uppercase tracking-[0.15em] px-5 py-1.5 rounded-full shadow-md" data-id="element-1091">
                    Recomandat
                  </span>}

                {/* Header */}
                <div className="text-center pb-6 border-b border-white/10" data-id="element-1092">
                  <h3 className="font-playfair text-[28px] md:text-[32px] font-bold mb-2" data-id="element-1093">
                    {pkg.name}
                  </h3>
                  <p className="font-montserrat text-[12px] uppercase tracking-[0.15em] text-white/60" data-id="element-1094">
                    Ce este inclus și ce nu este inclus
                  </p>
                </div>

                {/* Feature groups */}
                <div className="flex-1 py-6 space-y-8" data-id="element-1095">
                  {featureGroups.map(group => <div key={group.title} data-id="element-1096">
                      <h4 className="font-montserrat font-bold text-[12px] uppercase tracking-[0.15em] text-brand-gold mb-4" data-id="element-1097">
                        {group.title}
                      </h4>
                      <ul className="space-y-3" data-id="element-1098">
                        {group.features.map((feature, i) => {
                    const included = pkg.included.includes(feature);
                    return <li key={i} className="flex items-start gap-3" data-id="element-1099">
                              {included ? <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-[5px] bg-brand-gold flex items-center justify-center" data-id="element-1100">
                                  <CheckCircle2Icon className="text-brand-navy" size={14} strokeWidth={3} data-id="element-1101" />
                                </span> : <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-[5px] bg-white/10 flex items-center justify-center" data-id="element-1102">
                                  <XIcon className="text-white/40" size={13} strokeWidth={3} data-id="element-1103" />
                                </span>}
                              <span className={`font-source text-[15px] leading-snug ${included ? 'text-white/90' : 'text-white/35 line-through'}`} data-id="element-1104">
                                {feature}
                              </span>
                            </li>;
                  })}
                      </ul>
                    </div>)}
                </div>

                {/* Price panel */}
                <div className="mt-auto" data-id="element-1105">
                  <div className="bg-brand-ivory rounded-[16px] p-6 text-center mb-6" data-id="element-1106">
                    <p className="font-montserrat font-bold text-[12px] uppercase tracking-[0.15em] text-brand-text/60 mb-1" data-id="element-1107">
                      Valoare reală
                    </p>
                    <p className="font-playfair text-[20px] text-brand-text/50 line-through mb-3" data-id="element-1108">
                      {pkg.oldPrice}
                    </p>
                    <p className="font-montserrat font-bold text-[12px] uppercase tracking-[0.15em] text-brand-coral mb-1" data-id="element-1109">
                      Preț special
                    </p>
                    <p className="font-playfair text-[40px] font-bold text-brand-navy leading-none mb-2" data-id="element-1110">
                      {pkg.price}
                    </p>
                    <p className="font-source text-[14px] text-brand-text/70" data-id="element-1111">
                      {pkg.note}
                    </p>
                  </div>

                  <button onClick={() => {
                setActiveTab(pkg.key);
                document.getElementById('inscriere')?.scrollIntoView({
                  behavior: 'smooth'
                });
              }} className="btn-primary w-full text-center uppercase tracking-wide" data-id="element-1112">
                    {pkg.cta}
                  </button>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 8 — ÎNSCRIERE (EMBED FORM) */}
      <section id="inscriere" className="bg-brand-navy section-padding text-white" data-id="element-1113">
        <div className="container-custom" data-id="element-1114">
          <div className="max-w-4xl mx-auto" data-id="element-1115">
            <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-white text-center mb-8" data-id="element-1116">
              Înscriere în Programul CRESC
            </h2>

            {/* Tabs */}
            <div className="flex justify-center gap-4 mb-8" data-id="element-1117">
              <button onClick={() => setActiveTab('standard')} className={`px-6 py-3 rounded-full font-montserrat font-bold text-[14px] transition-colors ${activeTab === 'standard' ? 'bg-brand-gold text-brand-navy' : 'bg-white/10 text-white hover:bg-white/20'}`} data-id="element-1118">
                CRESC Standard
              </button>
              <button onClick={() => setActiveTab('premium')} className={`px-6 py-3 rounded-full font-montserrat font-bold text-[14px] transition-colors ${activeTab === 'premium' ? 'bg-brand-gold text-brand-navy' : 'bg-white/10 text-white hover:bg-white/20'}`} data-id="element-1119">
                CRESC Premium
              </button>
            </div>

            <div className="bg-white rounded-[24px] overflow-hidden min-h-[600px]" data-id="element-1120">
              {activeTab === 'standard' ? <iframe src="https://api.leadconnectorhq.com/widget/form/jYuDotzene5Wditr6qPd" style={{
              width: '100%',
              height: '100%',
              minHeight: '600px',
              border: 'none'
            }} id="inline-jYuDotzene5Wditr6qPd" data-layout="{'id':'INLINE'}" data-trigger-type="alwaysShow" data-trigger-value="" data-activation-type="alwaysActivated" data-activation-value="" data-deactivation-type="neverDeactivate" data-deactivation-value="" data-form-name="Form 1" data-height="600" data-layout-iframe-id="inline-jYuDotzene5Wditr6qPd" data-form-id="jYuDotzene5Wditr6qPd" title="Form 1" data-id="element-1121" /> : <iframe src="https://api.leadconnectorhq.com/widget/form/adwqQVH1Z8DHokX2kRH9" style={{
              width: '100%',
              height: '100%',
              minHeight: '600px',
              border: 'none'
            }} id="inline-adwqQVH1Z8DHokX2kRH9" data-layout="{'id':'INLINE'}" data-trigger-type="alwaysShow" data-trigger-value="" data-activation-type="alwaysActivated" data-activation-value="" data-deactivation-type="neverDeactivate" data-deactivation-value="" data-form-name="Form 2" data-height="600" data-layout-iframe-id="inline-adwqQVH1Z8DHokX2kRH9" data-form-id="adwqQVH1Z8DHokX2kRH9" title="Form 2" data-id="element-1122" />}
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 9 — TESTIMONIALE */}
      <section className="bg-brand-ivory section-padding" data-id="element-1123">
        <div className="container-custom" data-id="element-1124">
          <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy text-center mb-12" data-id="element-1125">
            Ce spun mamele care deja au lucrat cu mine
          </h2>
          <ReviewsGallery data-id="element-1126" />
        </div>
      </section>

      {/* SECȚIUNEA 10 — FAQ */}
      <FAQSection faqs={faqs} data-id="element-1127" />
    </motion.div>;
}